import React from "react";
import styled from "styled-components";
import { makeStyles } from "@mui/styles";
import { spacing } from "@mui/system";
import {
  Box,
  Card,
  CardContent,
  FormGroup,
  Typography,
  Checkbox,
  Select as MuiSelect,
  TextField as MuiTextField,
  Button as MuiButton,
  FormControl,
  InputLabel,
  MenuItem,
} from "@mui/material";
import PropTypes from "prop-types";
import { connect, useDispatch } from "react-redux";
import { put, post } from "../api/actions";

import { setSnackbar } from "../../snackbar/api/snackbar";
import FormControlLabel from "@mui/material/FormControlLabel";
import { formatPhoneNumber } from "../../../setup/helpers";

const TextField = styled(MuiTextField)(spacing);
const Select = styled(MuiSelect)(spacing);

const PropertyManagerForm = (props) => {
  const useStyles = makeStyles((theme) => ({
    container: {
      display: "flex",
      flexWrap: "wrap",
    },
    loadingIcon: {
      position: "absolute",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
    },
  }));

  const handleSelectedAccountStatusChange = (event) => {
    onFieldChange(event);
    //setAccountState(event.target.value);
  };

  const save = () => {
    const pm = props.propertyManagers.data.propertyManager;
    const newPm = {
      id: pm.id,
      uuid: pm.uuid,
      name: pm.name,
      address1: pm.address1,
      address2: pm.address2,
      city: pm.city,
      state: pm.state,
      zip: pm.zip,
      phone: pm.phone,
      statusId: pm.statusId,
      email: pm.email,
      contactName: pm.contactName,
      billingEmail: pm.billingEmail,
      delinquencyEmail: pm.delinquencyEmail,
      stopLateFee: pm.stopLateFee,
      stopSetupFee: pm.stopSetupFee,
      stopPayments: pm.stopPayments,
      creditLateFeesOnMoveOut: pm.creditLateFeesOnMoveOut,
      // TODO: Re-enable after apl-property-service deploys notifyPmFinalBills schema + migration
      // notifyPmFinalBills: pm.notifyPmFinalBills,
      updateAllSubAccounts: true,
      developerId: pm.developerId,
      notes: pm.notes,
    };
    if (!newPm.id) {
      props.post(newPm).then(() => {
        if (props.propertyManagers.postPropertyManagerError) {
          dispatch(
            setSnackbar(true, "error", "Could not create property manager")
          );
        } else {
          dispatch(setSnackbar(true, "success", "Property Manager Created!"));
        }
        props.onUpdate();
      });
    } else {
      props.put(newPm).then((res) => {
        if (props.propertyManagers.putPropertyManagerError) {
          dispatch(
            setSnackbar(true, "error", "Could not update property manager")
          );
        } else {
          dispatch(setSnackbar(true, "success", "Property Manager Updated!"));
        }
        props.onUpdate();
      });
    }
  };

  const onFieldChange = (event) => {
    props.propertyManagers.data.propertyManager[event.target.name] =
      event.target.value;
  };

  const toggleSetupFee = () => {
    setStopSetUpFee(!stopSetupFee);
    props.propertyManagers.data.propertyManager.stopSetupFee = !stopSetupFee;
  };

  const toggleLateFee = () => {
    setStopLateFee(!stopLateFee);
    props.propertyManagers.data.propertyManager.stopLateFee = !stopLateFee;
  };

  const toggleStopPayments = () => {
    setStopPayments(!stopPayments);
    props.propertyManagers.data.propertyManager.stopPayments = !stopPayments;
  };

  const toggleCreditLateFeesOnMoveOut = () => {
    setCreditLateFeesOnMoveOut(!creditLateFeesOnMoveOut);
    props.propertyManagers.data.propertyManager.creditLateFeesOnMoveOut = !creditLateFeesOnMoveOut;
  };

  // TODO: Re-enable after apl-property-service deploys notifyPmFinalBills schema + migration
  // const toggleNotifyPmFinalBills = () => {
  //   setNotifyPmFinalBills(!notifyPmFinalBills);
  //   props.propertyManagers.data.propertyManager.notifyPmFinalBills = !notifyPmFinalBills;
  // };

  // Handles vendor drop down
  const pmRecord = props.propertyManagers.data.propertyManager;
  const flagOn = (value) => value == 1 || value === true;
  const [stopSetupFee, setStopSetUpFee] = React.useState(flagOn(pmRecord?.stopSetupFee));
  const [stopLateFee, setStopLateFee] = React.useState(flagOn(pmRecord?.stopLateFee));
  const [stopPayments, setStopPayments] = React.useState(flagOn(pmRecord?.stopPayments));
  const [creditLateFeesOnMoveOut, setCreditLateFeesOnMoveOut] = React.useState(flagOn(pmRecord?.creditLateFeesOnMoveOut));

  // The edit dialog is keepMounted, so this form exists before the PM payload arrives.
  // useState will not pick up stopPayments (or the other flags) unless we sync them.
  React.useEffect(() => {
    if (!pmRecord) return;
    setStopSetUpFee(flagOn(pmRecord.stopSetupFee));
    setStopLateFee(flagOn(pmRecord.stopLateFee));
    setStopPayments(flagOn(pmRecord.stopPayments));
    setCreditLateFeesOnMoveOut(flagOn(pmRecord.creditLateFeesOnMoveOut));
  }, [
    pmRecord?.uuid,
    pmRecord?.stopSetupFee,
    pmRecord?.stopLateFee,
    pmRecord?.stopPayments,
    pmRecord?.creditLateFeesOnMoveOut,
  ]);
  // const [notifyPmFinalBills, setNotifyPmFinalBills] = React.useState(props.propertyManagers.data.propertyManager?.notifyPmFinalBills == 1 ? true : false);
  const [PropertyManagerState, setPropertyManagerState] = React.useState("");
  const classes = useStyles();
  const dispatch = useDispatch();

  if (props.propertyManagers.propertyManagerIsLoading || props.own) {
    return (
      <Card mb={6}>
        <CardContent>
          <Typography variant="h3" gutterBottom>
            Loading...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Name"
              type="text"
              onChange={onFieldChange}
              name="name"
              defaultValue={props.propertyManagers.data.propertyManager.name}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Phone"
              type="text"
              onChange={onFieldChange}
              name="phone"
              defaultValue={formatPhoneNumber(props.propertyManagers.data.propertyManager.phone)}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Address 1"
              type="text"
              onChange={onFieldChange}
              name="address1"
              defaultValue={props.propertyManagers.data.propertyManager.address1}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Address 2"
              type="text"
              onChange={onFieldChange}
              name="address2"
              defaultValue={props.propertyManagers.data.propertyManager.address2}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="City"
              type="text"
              onChange={onFieldChange}
              name="city"
              defaultValue={props.propertyManagers.data.propertyManager.city}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="State"
              type="text"
              onChange={onFieldChange}
              name="state"
              defaultValue={props.propertyManagers.data.propertyManager.state}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Zip"
              type="text"
              onChange={onFieldChange}
              name="zip"
              defaultValue={props.propertyManagers.data.propertyManager.zip}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="statusIdLabel">Status</InputLabel>
            <Select
              labelId="statusId"
              id="statusId"
              label="Status"
              onChange={onFieldChange}
              defaultValue={props.propertyManagers.data.propertyManager.statusId}
              name="statusId"
            >
              {props.apl.genericTypes.propertyManagerStatus.data.map(
                (item, index) => (
                  <MenuItem key={item.id} id="AccountStatusOption1" value={item.id}>
                    {item.description}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          <TextField
            label="Contact Name"
            type="text"
            onChange={onFieldChange}
            name="contactName"
            defaultValue={props.propertyManagers.data.propertyManager.contactName}
            required
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Email"
              type="text"
              onChange={onFieldChange}
              name="email"
              defaultValue={props.propertyManagers.data.propertyManager.email}
              required
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Billing Email"
              type="text"
              onChange={onFieldChange}
              name="billingEmail"
              defaultValue={props.propertyManagers.data.propertyManager.billingEmail}
              required
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Delinquency Email"
              type="text"
              onChange={onFieldChange}
              name="delinquencyEmail"
              defaultValue={props.propertyManagers.data.propertyManager.delinquencyEmail}
              required
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="developerIdLabel">Developer</InputLabel>
            <Select
              labelId="developerId"
              id="developerId"
              label="Developer"
              onChange={onFieldChange}
              defaultValue={props.propertyManagers.data.propertyManager.developerId}
              name="developerId"
            >
              {(props.developers?.data?.developersList?.items || []).map(
                (item, index) => (
                  <MenuItem key={item.id} id="DeveloperOption" value={item.id}>
                    {item.name}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          <TextField
            label="Notes"
            type="text"
            onChange={onFieldChange}
            name="notes"
            defaultValue={props.propertyManagers.data.propertyManager.notes}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={stopSetupFee}
                    onChange={toggleSetupFee}
                    name="stopSetupFee"
                  />
                }
                label="Stop Setup Fee"
              />
            </FormGroup>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={stopLateFee}
                    onChange={toggleLateFee}
                    name="stopLateFee"
                  />
                }
                label="Stop Late Fee"
              />
            </FormGroup>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={stopPayments}
                    onChange={toggleStopPayments}
                    name="stopPayments"
                  />
                }
                label="Stop Payments"
              />
            </FormGroup>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={creditLateFeesOnMoveOut}
                    onChange={toggleCreditLateFeesOnMoveOut}
                    name="creditLateFeesOnMoveOut"
                  />
                }
                label="Credit Late Fees on Move-Out"
              />
            </FormGroup>
            {/* TODO: Re-enable after apl-property-service deploys notifyPmFinalBills schema + migration
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notifyPmFinalBills}
                    onChange={toggleNotifyPmFinalBills}
                    name="notifyPmFinalBills"
                  />
                }
                label="Notify PM Final Bills"
              />
            </FormGroup>
            */}
            <Box sx={{ marginLeft: 'auto' }}>
              <MuiButton variant="contained" onClick={save} color="secondary">
                Save
              </MuiButton>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Component Properties
PropertyManagerForm.propTypes = {
  propertyManagers: PropTypes.object.isRequired,
  owners: PropTypes.object.isRequired,
  propertyManagerStatuses: PropTypes.object.isRequired,
  put: PropTypes.object.isRequired,
  post: PropTypes.object.isRequired,
  apl: PropTypes.object.isRequired,
  developers: PropTypes.object.isRequired
};

function PropertyManagerFormState(state) {
  return {
    propertyManagers: state.propertyManagers,
    owners: state.owners,
    propertyManagerStatuses: state.propertyManagerStatuses,
    apl: state.apl,
    developers: state.developers
  };
}
export default connect(PropertyManagerFormState, { put, post })(
  PropertyManagerForm
);
